import { forwardRef, useCallback, useState, type MouseEvent } from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import { createShouldForwardStyledProp } from '../../styles';
import { IconButton } from '../Button';
import { FormControlSizes } from '../Form/types';
import { IconName } from '../Icons';
import { Text } from '../Text';
import { tabStyle, tabStyleProps } from './styles';
import { TabEditor } from './TabEditor';
import type { TabProps } from './types';

const NavTabWrapper = styled.a.withConfig({
  shouldForwardProp: createShouldForwardStyledProp(tabStyleProps),
})<TabProps & { to?: LinkProps['to'] }>`
  ${tabStyle};
  background: transparent;
`;

export interface NavTabProps
  extends Omit<TabProps, 'to'>, Omit<LinkProps, 'prefix' | 'onClick' | 'onMouseDown' | 'className'> {
  labelMarginBottom?: string;
}

export const NavTab = styled(
  forwardRef(
    (
      {
        label,
        prefix,
        suffix,
        editable = false,
        closable = false,
        reorderable = false,
        isSelected,
        onRename,
        onCancel,
        onRemove,
        isAddingTab = false,
        isDragging = false,
        disabled = false,
        showDot,
        dotVariant,
        mixpanelEvent,
        isTemporary,
        maxTabLength,
        tooltip,
        usingSmartLabel,
        to = '/',
        labelMarginBottom,
        ...props
      }: NavTabProps,
      ref
    ) => {
      const [isEditing, setEditing] = useState(isAddingTab);
      const handleRename = useCallback(
        (value: string) => {
          if (value) {
            onRename?.(value);
            setEditing(false);
          }
        },
        [onRename]
      );
      const handleCancel = useCallback(() => {
        onCancel?.();
        setEditing(false);
        if (isAddingTab) {
          onRemove?.();
        }
      }, [onCancel, onRemove, isAddingTab]);

      const handleRemove = useCallback(
        (e: MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation();
          e.preventDefault();
          onRemove?.();
          return false;
        },
        [onRemove]
      );

      const isExternal = typeof to === 'string' && (to.startsWith('//') || to.startsWith('http'));
      const theme = useTheme();

      return (
        <NavTabWrapper
          ref={ref}
          role="tab"
          as={isExternal ? 'a' : Link}
          aria-selected={isSelected}
          isSelected={isSelected}
          isDragging={isDragging}
          disabled={disabled}
          onDoubleClick={() => editable && setEditing(true)}
          {...(isExternal ? { href: to } : { to })}
          {...props}
        >
          {isEditing ? (
            <TabEditor initialValue={label} onSubmit={handleRename} onCancel={handleCancel} />
          ) : (
            <>
              {!!prefix && prefix}
              {label && <Text mb={labelMarginBottom}>{label}</Text>}
              {!!suffix && suffix}
              {closable && (
                <IconButton
                  icon={IconName.Close}
                  onClick={handleRemove}
                  onTouchEnd={e => e.stopPropagation()}
                  ghost={true}
                  size={FormControlSizes.Tiny}
                  style={{ width: 'auto', marginLeft: theme.spacing4 }}
                  color={theme.colorTextDefault}
                />
              )}
            </>
          )}
        </NavTabWrapper>
      );
    }
  )
)``;
